import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, DATE_FORMAT } from "const"
import { MtDriveVersion, MtDriveVersionTag } from "server/models"
import {
    StateType,
    useInput,
    qidIsOpened,
} from "client/utils"
import {
    StoreType,
    VersionValidation,
    SaveDriveVersion,
    SaveDriveVersionTags,
    closeModal,
    setDriveVersionEdit,
    setDriveVersionReloadList,
    setDriveVersionReloadEdit,
    createFileName,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    UserDropdown,
    DatePicker,
    DateFormatter,
    RadioButton,
    Feedback,
    Button,
    SpinnerIcon,
    DivInput,
    UserIcon,
    Delimiter,
    TagEditor,
} from "client/components"

interface EditVersionProps {
    version: MtDriveVersion
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    version,
    editAccess,
}: EditVersionProps) => {
    const file = useSelector((state: StoreType) => state.drive.file.fileEdit)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(version.name || createFileName(file))
    const [revision, setRevision] = useInput(version.revision)
    const [reportNumber, setReportNumber] = useInput(version.reportNumber)

    const [owner, setOwner] = useState(version.owner)
    const [expiryDate, setExpiryDate] = useState(version.expiryDate)
    const [publicationDate, setPublicationDate] = useState(version.publicationDate)
    const [isDefault, setIsDefault] = useState(version.default)
    const [selectedTags, setSelectedTags] = useState<number[]>([])

    const [validation, setValidation] = useState<VersionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!version.tags) return
        const tagIds: number[] = []
        version.tags.forEach((tag) => {
            tagIds.push(tag.tagId)
        })
        setSelectedTags(tagIds)
    }, [version])

    const onClose = () => {
        if (!isMounted) return
        dispatch(closeModal()) // can be opened only in modal
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const versionTags: MtDriveVersionTag[] = []
        selectedTags.forEach((tagId) => {
            versionTags.push({
                versionId: version.id,
                tagId,
            })
        })

        const newVersionData: MtDriveVersion = {
            ...version,
            name,
            owner,
            revision,
            expiryDate,
            publicationDate,
            default: isDefault,
            reportNumber,
            tags: versionTags,
        }

        Promise.all([
            SaveDriveVersion(newVersionData, lang),
            SaveDriveVersionTags(version.id, versionTags),
        ]).then((res) => {
            if (isMounted) {
                setValidation(res[0].validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res[0].validation) return

            dispatch(setDriveVersionEdit(newVersionData))
            dispatch(setDriveVersionReloadList(false))
            setTimeout(() => {
                dispatch(setDriveVersionReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setDriveVersionReloadEdit(false))
                setTimeout(() => {
                    dispatch(setDriveVersionReloadEdit(true))
                }, 0)
            }
        })
    }

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const versionOwner = version.owner && userList[version.owner]
    const qidPageOpened = qidIsOpened()
    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.revision}</Label>
                        <Input type="text" value={revision} onChange={setRevision} readOnly={!editAccess} />
                        <Feedback>{validation && validation.revision}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.owner}</Label>
                        {
                            editAccess && (
                                <UserDropdown
                                    selectedId={owner}
                                    listIds={accountUsers}
                                    title={lang.changeOwner}
                                    showDelete={false}
                                    onChange={(userId) => setOwner(userId)}
                                />
                            )
                        }
                        {
                            !editAccess
                            && <DivInput><UserIcon user={versionOwner} size="sm" /></DivInput>
                        }
                        <Feedback>{validation && validation.owner}</Feedback>
                    </FormGroup>
                </Col>
                {
                    qidPageOpened && (
                        <Col xs={12}>
                            <FormGroup>
                                <Label>{lang.expiryDate}</Label>
                                {
                                    editAccess
                                        ? (
                                            <DatePicker
                                                selected={expiryDate ? new Date(expiryDate) : null}
                                                onChange={(date: Date) => setExpiryDate(date)}
                                                dateFormat={DATE_FORMAT}
                                                isClearable
                                            />
                                        ) : (
                                            <DivInput widePadding>
                                                <DateFormatter
                                                    date={expiryDate}
                                                    defaultStyles
                                                    showTime={false}
                                                />
                                            </DivInput>
                                        )
                                }
                                <Feedback>{validation && validation.expiryDate}</Feedback>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.publicationDate}</Label>
                                {
                                    editAccess
                                        ? (
                                            <DatePicker
                                                selected={publicationDate ? new Date(publicationDate) : null}
                                                onChange={(date: Date) => setPublicationDate(date)}
                                                dateFormat={DATE_FORMAT}
                                                isClearable
                                            />
                                        ) : (
                                            <DivInput widePadding>
                                                <DateFormatter
                                                    date={publicationDate}
                                                    defaultStyles
                                                    showTime={false}
                                                />
                                            </DivInput>
                                        )
                                }
                                <Feedback>{validation && validation.publicationDate}</Feedback>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.default}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={isDefault}
                                        onChange={() => setIsDefault(true)}
                                        title={lang.yes}
                                        readOnly={!editAccess}
                                    />
                                    <RadioButton
                                        checked={!isDefault}
                                        onChange={() => setIsDefault(false)}
                                        title={lang.no}
                                        readOnly={!editAccess}
                                    />
                                </FormGroup>
                            </FormGroup>
                            <FormGroup>
                                <Label>{lang.reportNumber}</Label>
                                <Input type="text" value={reportNumber} onChange={setReportNumber} readOnly={!editAccess} />
                                <Feedback>{validation && validation.reportNumber}</Feedback>
                            </FormGroup>
                            <Delimiter />
                            <FormGroup>
                                <Label>{lang.tags}</Label>
                                <TagEditor
                                    selectedIds={selectedTags}
                                    onChange={(tagIds) => setSelectedTags(tagIds)}
                                    readOnly={!editAccess}
                                />
                            </FormGroup>
                        </Col>
                    )
                }
                {
                    editAccess && (
                        <Col xs={12}>
                            <Button variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>
                            <Button variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>
                            <Button row onClick={onClose}>{lang.cancel}</Button>
                        </Col>
                    )
                }
            </Row>
        </PageForm>
    )
}
export default Form
